import { BALL, FIELD, PADDLE, VIEW } from '../core/config.js';
import { enforceMinVertical, pushTrail, setBallAngle } from '../entities/ball.js';
import { dohBounds } from '../entities/doh.js';
import { toGrid } from '../entities/brickfield.js';

/**
 * Ball physics and every collision response in the playfield.
 *
 * The ball is advanced in sub-steps no larger than its own radius so it can
 * never tunnel through a brick, even at the top of the speed ramp.
 */

const CLAMP = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * @param {object} ball
 * @param {number} dt
 * @param {object} world { field, paddle, doh, catchMode, events }
 * @returns {boolean} false when the ball fell off the bottom
 */
export function stepBall(ball, dt, world) {
  const distance = Math.hypot(ball.vx, ball.vy) * dt;
  const steps = Math.max(1, Math.ceil(distance / (ball.r * 0.85)));
  const sub = dt / steps;

  for (let i = 0; i < steps; i += 1) {
    ball.x += ball.vx * sub;
    ball.y += ball.vy * sub;

    resolveWalls(ball, world);
    resolveBricks(ball, world);
    if (world.doh) resolveDoh(ball, world);
    resolvePaddle(ball, world);

    if (ball.y - ball.r > VIEW.H) {
      world.events.onLost?.(ball);
      return false;
    }
    if (ball.stuck) break;
  }

  pushTrail(ball);
  return true;
}

function resolveWalls(ball, world) {
  let bounced = false;

  if (ball.x - ball.r < FIELD.left) {
    ball.x = FIELD.left + ball.r;
    ball.vx = Math.abs(ball.vx);
    bounced = true;
  } else if (ball.x + ball.r > FIELD.right) {
    ball.x = FIELD.right - ball.r;
    ball.vx = -Math.abs(ball.vx);
    bounced = true;
  }

  if (ball.y - ball.r < FIELD.top) {
    ball.y = FIELD.top + ball.r;
    ball.vy = Math.abs(ball.vy);
    bounced = true;
  }

  if (bounced) world.events.onWall?.(ball);
}

function resolveBricks(ball, world) {
  const { field } = world;
  if (!field) return;

  const min = toGrid(ball.x - ball.r, ball.y - ball.r);
  const max = toGrid(ball.x + ball.r, ball.y + ball.r);

  let target = null;
  let bestOverlap = -1;
  let axis = 'y';
  let push = 0;

  for (let row = min.row; row <= max.row; row += 1) {
    for (let col = min.col; col <= max.col; col += 1) {
      const brick = field.at(col, row);
      if (!brick) continue;

      const bcx = brick.x + brick.w / 2;
      const bcy = brick.y + brick.h / 2;
      const ox = ball.r + brick.w / 2 - Math.abs(ball.x - bcx);
      const oy = ball.r + brick.h / 2 - Math.abs(ball.y - bcy);
      if (ox <= 0 || oy <= 0) continue;

      const depth = Math.min(ox, oy);
      if (depth > bestOverlap) {
        bestOverlap = depth;
        target = brick;
        if (ox < oy) {
          axis = 'x';
          push = Math.sign(ball.x - bcx) * ox;
        } else {
          axis = 'y';
          push = Math.sign(ball.y - bcy) * oy;
        }
      }
    }
  }

  if (!target) return;

  if (axis === 'x') {
    ball.x += push || ball.r;
    ball.vx = Math.sign(push || 1) * Math.abs(ball.vx);
  } else {
    ball.y += push || ball.r;
    ball.vy = Math.sign(push || 1) * Math.abs(ball.vy);
  }

  const result = field.damage(target);
  world.events.onBrick?.(target, result, ball);
  enforceMinVertical(ball);
}

function resolveDoh(ball, world) {
  const { doh } = world;
  const box = dohBounds(doh);
  const bcx = (box.left + box.right) / 2;
  const bcy = (box.top + box.bottom) / 2;
  const halfW = doh.w / 2;
  const halfH = doh.h / 2;

  const ox = ball.r + halfW - Math.abs(ball.x - bcx);
  const oy = ball.r + halfH - Math.abs(ball.y - bcy);
  if (ox <= 0 || oy <= 0) return;

  if (ox < oy) {
    ball.x += Math.sign(ball.x - bcx) * ox;
    ball.vx = Math.sign(ball.x - bcx) * Math.abs(ball.vx);
  } else {
    ball.y += Math.sign(ball.y - bcy) * oy;
    ball.vy = Math.sign(ball.y - bcy) * Math.abs(ball.vy);
  }

  world.events.onDoh?.(ball);
  enforceMinVertical(ball);
}

function resolvePaddle(ball, world) {
  const { paddle } = world;
  if (!paddle || ball.stuck) return;

  const left = paddle.cx - paddle.w / 2;
  const right = paddle.cx + paddle.w / 2;
  const top = paddle.y;
  const bottom = paddle.y + paddle.h;

  if (
    ball.x + ball.r < left ||
    ball.x - ball.r > right ||
    ball.y + ball.r < top ||
    ball.y - ball.r > bottom
  ) {
    return;
  }

  // Coming down onto the face of the paddle: the classic angled deflection.
  if (ball.vy > 0 && ball.y <= top + paddle.h * 0.6) {
    ball.y = top - ball.r - 0.01;
    let offset = CLAMP((ball.x - paddle.cx) / (paddle.w / 2), -1, 1);

    // A dead-centre hit would fire the ball perfectly vertically, and a player
    // tracking it exactly would then bounce it in that one column forever.
    // Keep a sliver of sideways travel, carrying on the way it was going.
    if (Math.abs(offset) < BALL.minOffset) {
      const drift = ball.vx !== 0 ? Math.sign(ball.vx) : Math.random() < 0.5 ? -1 : 1;
      offset = BALL.minOffset * drift;
    }

    const english = CLAMP((paddle.vx * PADDLE.spinTransfer) / 120, -0.26, 0.26);
    const angle = CLAMP(
      -Math.PI / 2 + offset * BALL.maxDeflect + english,
      -Math.PI / 2 - BALL.maxDeflect,
      -Math.PI / 2 + BALL.maxDeflect,
    );
    setBallAngle(ball, angle);
    world.events.onPaddle?.(ball, offset);
    return;
  }

  // Clipped the shoulder of the paddle: shove it clear sideways.
  const side = ball.x < paddle.cx ? -1 : 1;
  ball.x = side < 0 ? left - ball.r : right + ball.r;
  ball.vx = side * Math.abs(ball.vx);
  ball.vy = -Math.abs(ball.vy);
  world.events.onPaddle?.(ball, side);
}

/**
 * Laser bolt against the brick grid.
 * @returns {object|null} the brick that was struck
 */
export function boltHitsBrick(bolt, field) {
  if (!field) return null;
  const min = toGrid(bolt.x - bolt.w / 2, bolt.y);
  const max = toGrid(bolt.x + bolt.w / 2, bolt.y + bolt.h);
  for (let row = min.row; row <= max.row; row += 1) {
    for (let col = min.col; col <= max.col; col += 1) {
      const brick = field.at(col, row);
      if (brick) return brick;
    }
  }
  return null;
}

export function boltHitsDoh(bolt, doh) {
  if (!doh) return false;
  const box = dohBounds(doh);
  return (
    bolt.x + bolt.w / 2 > box.left &&
    bolt.x - bolt.w / 2 < box.right &&
    bolt.y < box.bottom &&
    bolt.y + bolt.h > box.top
  );
}

/** Where the ball should start when a round or life begins. */
export function serveBall(paddle) {
  return {
    x: paddle.cx,
    y: paddle.y - BALL.radius - 1,
  };
}

